import { ComparingZipAnalysisQueryParameters, ZipAnalysisQueryParameters, ZipClassAnalysisSearchOptionsResults, ZipCodeAnalysisSearchOptionsResults, ZipDetailsApiResponse } from "./zip-projects/interfaces";
import Project from "./project";
import { createDummyDropdownItem } from "./zip-projects/utility";
import { BulkRunSubmitResponseModel } from "./bulkRunSubmitResponseModel";
import { CreateZipProjectRequestModel } from "./zip-projects/interfaces/createZipProjectRequestModel";
import { ZipClassTrendChartItem, ZipCodeTrendChartItem } from "./zip-projects/interfaces/zipTrendChartItems";
import { ZipNcpProvidersResponse } from "./zip-projects/interfaces/zipNcpProvidersResponse";
import { NetworkAssessmentAnalysisType, ProjectRunConfigurationType } from "./enumerators";
import { ZipAnalysisSearchParameters, ZipClassAnalysisSearchParameters } from "./zip-projects/interfaces/zipAnalysisSearchParameters";
import { ZipAnalysisSearchOptions } from "./zip-projects/interfaces/zipAnalysisSearchOptions";
import { MemberMapResults, ProviderResults } from "./results/common/mapping";
import SpecialtyDetailFootnote from "@app/projects/results/adequacy/specialtyDetailFootnote";
import { ServiceAreaZip } from "@app/projects/zip-projects/maps/interfaces";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { SortDirections } from "../shared/waffles";
import { SimulateProjectionProvidersModel } from "./servicing-provider";
import { ZipCodePreviewDisplayModel } from "./zip-projects/interfaces/zipCodePreviewDisplayModel";
import { NcpEnum } from "./servicing-provider/types";
import { ZipClassAggregateResultDisplayModel } from "./zip-projects/interfaces/zipClassAggregateResultDisplayModel";
import { SimulateZipClassProjectionProvidersModel } from "./zip-projects/interfaces/simulateZipClassProjectionProvidersModel";
import { SimulateZipCodeTerminationModel } from "./zip-projects/interfaces/simulateZipCodeTerminationModel";
import { TermedZipCodePreviewDisplayModel } from "./zip-projects/interfaces/termedZipCodePreviewDisplayModel";
import ServiceBase from "@app/shared/serviceBase";
import { querystringify } from "@app/shared/utility/querystringify";
import CloneProjectModel from "@app/projects/clone/cloneProjectModel";

/** Handles communication across the wire when retrieving and interacting with zip projects. */
export default class ZipProjectService extends ServiceBase {
  private readonly endpointPrefix = "zipprojects";

  public async createZipProject(createZipProjectRequestModel: CreateZipProjectRequestModel): Promise<void> {
    return await this.makePost(this.endpointPrefix, createZipProjectRequestModel);
  }

  /**
   * Clones a project as a ZipCodeAnalysisProject with provided settings
   * @param project The clone project model.
   * @param runNow Indicates whether to run the project immediately after creation or not
   */
  public async cloneProject(cloneProjectModel: CloneProjectModel, runNow: boolean): Promise<Project> {
    const query = querystringify({
      runNow,
    });

    return await this.makePost(`${this.endpointPrefix}/clone?${query}`, cloneProjectModel);
  }

  // also known as "submit"
  public async runZipProject(projectId: number): Promise<void> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/submit`);
  }

  public async bulkRunZipProjects(projectIds: number[]): Promise<BulkRunSubmitResponseModel> {
    if (projectIds.length > 0) {
      return await this.makePost(`${this.endpointPrefix}/submit`, projectIds);
    }
    return {
      submitResults: [],
    }
  }

  public async getFilteredRegionSetIds(projectId: number, runConfigurationId: number, zipAnalysisQuery: ZipAnalysisSearchParameters): Promise<number[]> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-analysis-region-set-ids`, zipAnalysisQuery);
  }

  public async getZipCodeResults(projectId: number, runConfigurationId: number, zipAnalysisQuery: ZipAnalysisQueryParameters, signal?: AbortSignal): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(
      `${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-analysis`,
      zipAnalysisQuery,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      signal,
    );
    return response || [{
      data: [],
      hasExpandableSpecialties: false,
      projectRunConfigurationId: runConfigurationId,
      resultsFor: "---",
      specialtyLevelResults: {},
      totalNumberOfRecords: 0,
    }];
  }

  public async getZipClassResults(projectId: number, runConfigurationId: number, zipAnalysisQuery: ZipAnalysisQueryParameters, signal?: AbortSignal): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(
      `${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-class-aggregates`,
      zipAnalysisQuery,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      signal,
    );
    return response || [{
      data: [],
      hasExpandableSpecialties: false,
      projectRunConfigurationId: runConfigurationId,
      resultsFor: "---",
      specialtyLevelResults: {},
      totalNumberOfRecords: 0,
    }];
  }

  public async GetZipMemberResultsAsync(
    zipProjectId: number,
    runConfigurationId: number,
    zipCode: string,
    specialtyGroupCode: string,
  ): Promise<MemberMapResults[]> {
    const query = querystringify({ specialtyGroupCode, zipCode });
    return await this.makeGet<MemberMapResults[]>(`${this.endpointPrefix}/${zipProjectId}/run-configurations/${runConfigurationId}/member-results?${query}`);
  }

  public async getZipCodeCompareModeResults(projectId: number, zipAnalysisQuery: ComparingZipAnalysisQueryParameters): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(`${this.endpointPrefix}/${projectId}/compared-zip-analysis`, zipAnalysisQuery);
    const newResponse: ZipDetailsApiResponse[] = [];
    response.forEach((r, index) => {
      if (!r) {
        r = {
          data: [],
          hasExpandableSpecialties: false,
          projectRunConfigurationId: index === 0 ? zipAnalysisQuery.projectRunConfigurationId1 : zipAnalysisQuery.projectRunConfigurationId2,
          resultsFor: "---",
          specialtyLevelResults: {},
          totalNumberOfRecords: 0,
        };
      }
      newResponse.push(r);
    })
    return newResponse;
  }

  public async getZipClassCompareModeResults(projectId: number, zipAnalysisQuery: ComparingZipAnalysisQueryParameters): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(`${this.endpointPrefix}/${projectId}/compared-zip-class-aggregates`, zipAnalysisQuery);
    const newResponse: ZipDetailsApiResponse[] = [];
    response.forEach((r, index) => {
      if (!r) {
        r = {
          data: [],
          hasExpandableSpecialties: false,
          projectRunConfigurationId: index === 0 ? zipAnalysisQuery.projectRunConfigurationId1 : zipAnalysisQuery.projectRunConfigurationId2,
          resultsFor: "---",
          specialtyLevelResults: {},
          totalNumberOfRecords: 0,
        };
      }
      newResponse.push(r);
    })
    return newResponse;
  }

  // getZipClassAggregateFilters in the API
  public async getZipClassFilterOptions(projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number, signal?: AbortSignal): Promise<ZipAnalysisSearchOptions> {
    let apiResults: ZipClassAnalysisSearchOptionsResults[] = [];

    if (isSucceeded) {
      const body = !!compareToRunConfigurationId ? [compareToRunConfigurationId] : null;
      apiResults = await this.makePost<ZipClassAnalysisSearchOptionsResults[]>(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-class-aggregate-filters`, body, undefined, false, "application/json", true, undefined, signal);
    }

    return {
      city: [],
      countiesAndStates: [],
      county: [],
      metOverall: [
        {
          key: "all",
          name: "All",
        },
        ...[...(new Set(apiResults.flatMap((f) => f.filters.metOverallOptions || [])))].sort((a, b) => a > b ? -1 : 1).map(str => createDummyDropdownItem(str)),
      ],
      metPassPercent: [],
      specialtyGroupCode: [
        {
          display: "All Specialty Groups",
          key: "all",
          name: "All Specialty Groups",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.specialtyGroupCodes || [])))]).map(sg => {
          return {
            key: sg.specialtyGroupCode,
            name: `${sg.specialtyGroupCode} - ${sg.specialtyGroupDescription}`,
          };
        }),
      ],
      state: [],
      zipClass: [
        {
          display: "All Classifications",
          key: "all",
          name: "All Classifications",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.zipClasses || [])))].sort((a, b) => a > b ? 1 : -1) || []).map(str => createDummyDropdownItem(str)),
      ],
      zipCode: [],
    }
  }

  public async getZipClassAggregateFiltersForProjects(customProjectIds: number[], publishedOnly: boolean): Promise<string[]> {
      const response = await this.makePost<string[]>(`${this.endpointPrefix}/zip-class-aggregate-filters?publishedOnly=${publishedOnly}`, customProjectIds);
      return response ?? [];
  }

  public async getZipCodeFilterOptions(projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number, signal?: AbortSignal): Promise<ZipAnalysisSearchOptions> {
    let apiResults: ZipCodeAnalysisSearchOptionsResults[] = [];

    if (isSucceeded) {
      const body = !!compareToRunConfigurationId ? [compareToRunConfigurationId] : null;
      apiResults = await this.makePost<ZipCodeAnalysisSearchOptionsResults[]>(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-analysis-filters`, body, undefined, false, "application/json", true, undefined, signal);
    }

    return {
      city: [...(new Set(apiResults.flatMap((f) => f.filters.cities || [])))].sort((a, b) => a > b ? 1 : -1),
      countiesAndStates: apiResults.flatMap(f => f.filters.countiesAndStates).sort((a, b) => a.key > b.key ? 1 : -1),
      county: [
        {
          display: "All Counties",
          key: "all",
          name: "All Counties",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.counties || [])))].sort((a, b) => a > b ? 1 : -1)).map(str => createDummyDropdownItem(str)),
      ],
      metOverall: [],
      metPassPercent: [
        {
          key: "all",
          name: "All",
        },
        ...[...(new Set(apiResults.flatMap((f) => f.filters.metPassPercent || [])))].sort((a, b) => a > b ? -1 : 1).map(str => createDummyDropdownItem(str)),
      ],
      specialtyGroupCode: [
        {
          display: "All Specialty Groups",
          key: "all",
          name: "All Specialty Groups",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.specialtyGroupCodes || [])))]).map(sg => {
          return {
            key: sg.specialtyGroupCode,
            name: `${sg.specialtyGroupCode} - ${sg.specialtyGroupDescription}`,
          };
        }),
      ],
      state: [
        {
          display: "All States",
          key: "all",
          name: "All States",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.stateNames || [])))].sort((a, b) => a > b ? 1 : -1)).map(str => createDummyDropdownItem(str)),
      ],
      zipClass: [
        {
          display: "All Classifications",
          key: "all",
          name: "All Classifications",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.zipClasses || [])))].sort((a, b) => a > b ? 1 : -1) || []).map(str => createDummyDropdownItem(str)),
      ],
      zipCode: [...(new Set(apiResults.flatMap((f) => f.filters.zipCodes || [])))].sort((a, b) => a > b ? 1 : -1),
    };
  }

  public async publishZipProject(projectId: number, comment = ""): Promise<void> {
    return await this.makePut(`${this.endpointPrefix}/${projectId}/publish?comment=${encodeURIComponent(comment)}`);
  }

  public async bulkPublishZipProjects(projectIds: number[], comment: string): Promise<number[]> {
    if (projectIds.length > 0) {
      return await this.makePost<number[]>(`${this.endpointPrefix}/publish`, { ProjectIds: projectIds, PublishComment: comment });
    }
    return [];
  }

  public async retryZipAnalysisByJobId(overrideJobQueueName: string, runConfigurationId: number, jobId: number): Promise<void> {
    const query = querystringify({ overrideJobQueueName });
    return await this.makePost(`${this.endpointPrefix}/run-configurations/${runConfigurationId}/jobs/${jobId}/retry?${query}`);
  }

  public async getZipClassTrendChartData(projectId: number, searchParameters: ZipClassAnalysisSearchParameters): Promise<ZipClassTrendChartItem[]> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/zip-class-trend-graph-data`, searchParameters);
  }

  public async getZipCodeTrendChartData(projectId: number, searchParameters: ZipAnalysisSearchParameters): Promise<ZipCodeTrendChartItem[]> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/zip-code-trend-graph-data`, searchParameters);
  }

  public async processProviderChanges(projectId: number, reprocessFailure: boolean): Promise<void> {
    const query = querystringify({ reprocessFailure });
    return await this.makePost(`${this.endpointPrefix}/${projectId}/processproviderchanges?${query}`);
  }
  public async simulateZipCodeProjection(projectId: number, regionSetId: number, specialtyGroupCode: string, requestBody: SimulateProjectionProvidersModel, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, isProjectedTermed: boolean, isTermed: boolean, dualMembershipRunConfigurationType: ProjectRunConfigurationType): Promise<ZipCodePreviewDisplayModel> {
    if (!projectId || !regionSetId || !specialtyGroupCode || !requestBody.providerNetworkType || !requestBody.projectionType || !requestBody.providerIds) {
      return null;
    }

    return await this.makePost<ZipCodePreviewDisplayModel>(`${this.endpointPrefix}/${projectId}/region-sets/${regionSetId}/simulate-zip-code-projection?${querystringify({
      dualMembershipRunConfigurationType,
      isProjectedTermed,
      isTermed,
      networkAssessmentAnalysisType,
      specialtyGroupCode
    })}`,
      requestBody
    );
  }

  public async simulateZipClassProjection(projectId: number, regionSetId: number, specialtyGroupCode: string, requestBody: SimulateZipClassProjectionProvidersModel, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, isProjectedTermed: boolean, isTermed: boolean, dualMembershipRunConfigurationType: ProjectRunConfigurationType): Promise<ZipClassAggregateResultDisplayModel> {
    if (!projectId || !regionSetId || !specialtyGroupCode || !requestBody.providerNetworkType || !requestBody.projectionType || !requestBody.providerIds || !requestBody.projectedZipCodeAnalysis) {
        return null;
    }

    return await this.makePost<ZipClassAggregateResultDisplayModel>(`${this.endpointPrefix}/${projectId}/region-sets/${regionSetId}/simulate-zip-class-projection?${querystringify({
        dualMembershipRunConfigurationType,
        isProjectedTermed,
        isTermed,
        networkAssessmentAnalysisType,
        specialtyGroupCode
    })}`,
        requestBody
    );
  }

  public async simulateZipCodeTermination(projectId: number, regionSetId: number, specialtyGroupCode: string, requestBody: SimulateZipCodeTerminationModel, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, dualMembershipRunConfigurationType: ProjectRunConfigurationType): Promise<TermedZipCodePreviewDisplayModel> {
    if (!projectId || !regionSetId || !specialtyGroupCode || !requestBody.providerNetworkType || !requestBody.providerIds) {
      return null;
    }

    return await this.makePost<TermedZipCodePreviewDisplayModel>(`${this.endpointPrefix}/${projectId}/region-sets/${regionSetId}/simulate-zip-termination?${querystringify({
      dualMembershipRunConfigurationType,
      networkAssessmentAnalysisType,
      specialtyGroupCode
    })}`,
      requestBody
    );
  }

  /**
   * Servicing access points (provider map points) for a single zip code and specialty group.
   *
   * Zip counterpart of `ProjectService.servicingAccessPoints`, keyed on zip code rather than region-set id —
   * the API resolves the region-set specialty group from the zip analysis results. Always ServiceArea.
   */
  public async servicingAccessPoints(
    zipProjectId: number,
    runConfigurationId: number,
    zipCode: string,
    specialtyGroupCode: string,
    providerNetworkType: ProviderNetworkTypeEnum,
    signal?: AbortSignal,
  ): Promise<ProviderResults> {
    return await this.makeGet<ProviderResults>(
      `${this.endpointPrefix}/${zipProjectId}/run-configurations/${runConfigurationId}/zip-codes/${zipCode}/access-points/${providerNetworkType}`,
      { specialtyGroupCode },
      undefined,
      undefined,
      undefined,
      signal,
    );
  }

  /**
   * Adequacy-requirement footnote for a single zip code and specialty group.
   *
   * Zip counterpart of the county footnote from `ProjectService.regionDetailsByRegionSet`, keyed on zip code —
   * the API resolves the region-set specialty group and builds the footnote with the shared AdequacySummaryHelper.
   */
  public async getZipAdequacyFootnote(
    zipProjectId: number,
    runConfigurationId: number,
    zipCode: string,
    specialtyGroupCode: string,
    signal?: AbortSignal,
  ): Promise<SpecialtyDetailFootnote> {
    return await this.makeGet<SpecialtyDetailFootnote>(
      `${this.endpointPrefix}/${zipProjectId}/run-configurations/${runConfigurationId}/zip-codes/${zipCode}/adequacy-footnote`,
      { specialtyGroupCode },
      undefined,
      undefined,
      undefined,
      signal,
    );
  }

  /**
   * The distinct zip codes holding membership on a run configuration.
   *
   * Specialty-agnostic: a zip code comes back when any of its specialty groups has members. The ZIP map's Service
   * Area list is built from geography (every zip in each service-area county), so this is what tells it which of
   * those zip codes have anything to draw. Zip codes the analysis never wrote a row for are absent.
   */
  public async getZipCodesWithMembership(
    zipProjectId: number,
    runConfigurationId: number,
    signal?: AbortSignal,
  ): Promise<string[]> {
    return await this.makeGet<string[]>(
      `${this.endpointPrefix}/${zipProjectId}/run-configurations/${runConfigurationId}/zip-codes-with-membership`,
      undefined,
      undefined,
      undefined,
      undefined,
      signal,
    );
  }

  /**
   * The zip codes selected in a run configuration's service area, with the label and zip-point flag
   * the ZIP map's Service Area list renders. A fully-selected county contributes all of its zip codes.
   */
  public async getServiceAreaZipCodes(
    zipProjectId: number,
    runConfigurationId: number,
    signal?: AbortSignal,
  ): Promise<ServiceAreaZip[]> {
    return await this.makeGet<ServiceAreaZip[]>(
      `${this.endpointPrefix}/${zipProjectId}/run-configurations/${runConfigurationId}/service-area-zip-codes`,
      undefined,
      undefined,
      undefined,
      undefined,
      signal,
    );
  }

  /* oxlint-disable sort-keys */
  public async getZipNextClosestProviders(
    projectId: number,
    regionSetId: number,
    runConfigurationId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    exclusion: boolean,
    ncpFilterValue: NcpEnum,
    skip?: number,
    take?: number,
    sortedColumn?: string,
    sortDirection?: SortDirections,
    searchValue?: string): Promise<ZipNcpProvidersResponse> {
      return await this.makePost<ZipNcpProvidersResponse>(
        `nextClosestProvider/zipanalysis/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}`,
        {
          columnSubKey: "",
          networkAssessmentAnalysisType: networkAssessmentAnalysisType,
          exclusion: exclusion,
          ncpFilterValue: ncpFilterValue.toString(),
          searchValue: searchValue || "",
          skip: skip || 0,
          sortDirection: sortDirection || 'Ascending',
          sortedColumn: sortedColumn || "",
          specialtyGroupCode: specialtyGroupCode,
          take: take || 1000,
          //the folowing one is existing only in county project currently. so assigning default value.
          gainPercent: 'all',
          selectedEffectivenessValues: [],
          selectedEfficiencyValues: [],
          selectedLineOfBusiness: 'unknown',
          selectedVolumeLabels: [],
          selectedLocationConfidenceValues: [],
          selectedMotiveAppropriatenessValues: [],
          selectedNewPatientClaimsValues: [],
          selectedCareJourneyCostValues: [],
          selectedCareJourneyQualityValues: [],
          targetedType: "",
          selectedTopProcedures: [],
          selectedTopConditions: []
        });
  }
}

